"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";

const accent = "#577099";

export default function RegisterCustomerPage() {
  const router = useRouter();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const clearAlerts = () => {
    setMessage("");
    setError("");
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    clearAlerts();
    setLoading(true);

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail || !password) {
      setError("Имэйл болон нууц үг заавал бөглөнө үү.");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Нууц үг хамгийн багадаа 6 тэмдэгт байх ёстой.");
      setLoading(false);
      return;
    }

    if (password !== password2) {
      setError("Нууц үг давталт таарахгүй байна.");
      setLoading(false);
      return;
    }

    try {
      const res = await fetch("/api/customer", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "register",
          email: trimmedEmail,
          password,
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
        }),
      });

      let data = {};
      try {
        data = await res.json();
      } catch {
        /* хоосон body */
      }

      if (res.ok && data.success) {
        setMessage(
          data.emailSent
            ? "Бүртгэл амжилттай үүслээ. Баталгаажуулах/мэдэгдлийн имэйл илгээгдлээ. Нэвтрэх хуудас руу шилжиж байна…"
            : "Бүртгэл амжилттай үүслээ. Нэвтрэх хуудас руу шилжиж байна…"
        );
        setTimeout(() => router.push("/customer"), 3500);
        return;
      }

      if (res.status === 503 && data.error) {
        setError(data.error);
        return;
      }

      setError(
        data.message ||
          (res.status === 409
            ? "Энэ имэйл бүртгэлтэй байна."
            : "Алдаа гарлаа. Дахин оролдоно уу.")
      );
    } catch (err) {
      console.error("Register error:", err);
      setError("Сервертэй холбогдож чадсангүй. Дахин оролдоно уу.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-gray-200 bg-white px-3 py-3 mt-1 text-gray-900 placeholder:text-gray-400 outline-none transition-shadow focus:border-[#577099] focus:ring-2 focus:ring-[#577099]/25";

  return (
    <div className="min-h-[calc(100vh-8rem)] bg-gradient-to-b from-slate-50 via-blue-50/30 to-slate-50 px-4 py-10 pb-16">
      <div className="mx-auto flex max-w-md flex-col">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="rounded-2xl border border-gray-100/80 bg-white p-8 shadow-lg shadow-slate-200/60"
        >
          <p className="text-center text-xs font-medium uppercase tracking-wider text-[#577099]/90">
            Шинэ хэрэглэгч
          </p>
          <h1 className="mt-1 text-center text-2xl font-bold text-gray-900">
            Бүртгэл үүсгэх
          </h1>
          <p className="mt-2 text-center text-sm text-gray-600">
            Имэйлээрээ нэвтэрч, сэтгэгдэл үлдээж, дуртай газруудаа дагана уу.
          </p>

          <form onSubmit={handleRegister} className="mt-8 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="reg-first" className="text-sm font-medium text-gray-700">
                  Нэр
                </label>
                <input
                  id="reg-first"
                  type="text"
                  autoComplete="given-name"
                  placeholder="Нэр"
                  value={firstName}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    clearAlerts();
                  }}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="reg-last" className="text-sm font-medium text-gray-700">
                  Овог
                </label>
                <input
                  id="reg-last"
                  type="text"
                  autoComplete="family-name"
                  placeholder="Овог"
                  value={lastName}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    clearAlerts();
                  }}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label htmlFor="reg-email" className="text-sm font-medium text-gray-700">
                Имэйл <span className="text-red-500">*</span>
              </label>
              <input
                id="reg-email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearAlerts();
                }}
                required
                className={inputClass}
                aria-invalid={!!error && !message}
              />
            </div>

            <div>
              <label htmlFor="reg-password" className="text-sm font-medium text-gray-700">
                Нууц үг <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="reg-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Хамгийн багадаа 6 тэмдэгт"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    clearAlerts();
                  }}
                  required
                  minLength={6}
                  className={`${inputClass} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Нууц үг нуух" : "Нууц үг харуулах"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 mt-1 flex items-center px-3 text-gray-500 transition hover:text-gray-700 focus:outline-none focus:text-[#577099]"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="reg-password2" className="text-sm font-medium text-gray-700">
                Нууц үг давтах <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="reg-password2"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Дахин оруулна уу"
                  value={password2}
                  onChange={(e) => {
                    setPassword2(e.target.value);
                    clearAlerts();
                  }}
                  required
                  minLength={6}
                  className={`${inputClass} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Нууц үг нуух" : "Нууц үг харуулах"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 mt-1 flex items-center px-3 text-gray-500 transition hover:text-gray-700 focus:outline-none focus:text-[#577099]"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {message && (
              <div
                role="status"
                className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
              >
                {message}
              </div>
            )}

            {error && (
              <div
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
              >
                {error}
              </div>
            )}

            <motion.button
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              type="submit"
              disabled={loading}
              style={{ backgroundColor: accent }}
              className="w-full rounded-lg py-3.5 font-semibold text-white shadow-md transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? "Бүртгэж байна…" : "Бүртгэл үүсгэх"}
            </motion.button>
          </form>

          <p className="mt-8 text-center text-sm text-gray-600">
            Аль хэдийн бүртгэлтэй юу?{" "}
            <Link
              href="/customer"
              className="font-semibold text-[#577099] underline-offset-2 hover:underline"
            >
              Нэвтрэх
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
